/**
 * Agent Builder — shared TypeScript types.
 */

export interface AgentDefinition {
  id: string
  slug: string
  category: 'system' | 'custom'
  harness: 'custom' | 'mastra'
  displayName: string
  mascot: string
  icon: string
  shortDescription: string
  detailedDescription: string | null
  systemPrompt: string
  tools: string[]
  modelProvider: string
  modelId: string
  temperature: number
  maxOutputTokens: number
  maxToolTurns: number
  intentDescription: string
  intentKeywords: string[]
  acceptedFileTypes: string | null
  outputFormat: string
  sessionSticky: boolean
  guardrails: GuardrailsConfig
  accessType: string
  accessConfig: Record<string, unknown>
  toolRoute: string | null
  hasMcpApp: boolean
  mcpAppUrl: string | null
  comingSoonMessage: string | null
  systemPromptR2Key: string | null
  status: 'draft' | 'active' | 'disabled' | 'coming_soon' | 'tool_available'
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface GuardrailsConfig {
  piiRedaction?: boolean
  contentModeration?: boolean
  moderationThreshold?: number
}

export interface ToolCatalogItem {
  id: string
  slug: string
  displayName: string
  description: string
  inputSchema: Record<string, unknown>
  executorType: string
  executorConfig: Record<string, unknown>
  category: string
  status: string
  createdAt: string
}

export interface TestResult {
  text: string
  tokenUsage: Record<string, unknown> | null
  latencyMs: number
  error?: string
}

/** Form data for creating/editing an agent — camelCase for frontend, converted to snake_case by service */
export interface AgentFormData {
  displayName: string
  mascot: string
  shortDescription: string
  detailedDescription: string
  systemPrompt: string
  icon: string
  tools: string[]
  modelProvider: string
  modelId: string
  temperature: number
  maxOutputTokens: number
  maxToolTurns: number
  intentDescription: string
  intentKeywords: string[]
  acceptedFileTypes: string
  outputFormat: string
  sessionSticky: boolean
  guardrails: GuardrailsConfig
  accessType: string
  accessConfig: Record<string, unknown>
}

/** Maps API response to frontend type — handles both camelCase (CF) and snake_case (Studio) */
export function mapAgentDefinition(row: any): AgentDefinition {
  return {
    id: row.id,
    slug: row.slug,
    category: row.category ?? 'custom',
    harness: row.harness,
    displayName: row.displayName ?? row.display_name,
    mascot: row.mascot ?? 'pop',
    icon: row.icon,
    shortDescription: row.shortDescription ?? row.short_description,
    detailedDescription: row.detailedDescription ?? row.detailed_description,
    systemPrompt: row.systemPrompt ?? row.system_prompt,
    tools: row.tools ?? [],
    modelProvider: row.modelProvider ?? row.model_provider,
    modelId: row.modelId ?? row.model_id,
    temperature: row.temperature,
    maxOutputTokens: row.maxOutputTokens ?? row.max_output_tokens,
    maxToolTurns: row.maxToolTurns ?? row.max_tool_turns,
    intentDescription: row.intentDescription ?? row.intent_description,
    intentKeywords: row.intentKeywords ?? row.intent_keywords ?? [],
    acceptedFileTypes: row.acceptedFileTypes ?? row.accepted_file_types,
    outputFormat: row.outputFormat ?? row.output_format,
    sessionSticky: row.sessionSticky ?? row.session_sticky,
    guardrails: row.guardrails ?? {},
    accessType: row.accessType ?? row.access_type,
    accessConfig: row.accessConfig ?? row.access_config ?? {},
    toolRoute: row.toolRoute ?? row.tool_route ?? null,
    hasMcpApp: Boolean(row.hasMcpApp ?? row.has_mcp_app ?? false),
    mcpAppUrl: row.mcpAppUrl ?? row.mcp_app_url ?? null,
    comingSoonMessage: row.comingSoonMessage ?? row.coming_soon_message ?? null,
    systemPromptR2Key: row.systemPromptR2Key ?? row.system_prompt_r2_key ?? null,
    status: row.status,
    createdBy: row.createdBy ?? row.created_by,
    createdAt: row.createdAt ?? row.created_at,
    updatedAt: row.updatedAt ?? row.updated_at,
  }
}

export function mapToolCatalogItem(row: any): ToolCatalogItem {
  return {
    id: row.id,
    slug: row.slug,
    displayName: row.display_name,
    description: row.description,
    inputSchema: row.input_schema,
    executorType: row.executor_type,
    executorConfig: row.executor_config,
    category: row.category,
    status: row.status,
    createdAt: row.created_at,
  }
}
